import { createSelector } from '@reduxjs/toolkit';
import { getOptionValue } from 'software_renewal/redux/utils';

export const selectProducts = (state) =>
  state.softwareRenewal?.licenseInfo?.products;

export const selectLoading = (state) => state.softwareRenewal?.loading;

export const selectSerialNumber = (state) =>
  state.softwareRenewal?.serialNumber;

export const selectSerialNumberLoaded = (state) =>
  state.softwareRenewal?.serialNumberLoaded;

export const selectExpirationDate = (state) =>
  state.softwareRenewal?.licenseInfo?.expiration_date;

export const selectAddingToCart = (state) =>
  state.softwareRenewal?.addingToCart;

export const selectShowForgotSerialNumberModal = (state) =>
  state.softwareRenewal?.showForgotSerialNumberModal;

export const selectRecoveryEmailError = (state) => state.softwareRenewal?.error;

export const selectError = (state) => state.softwareRenewal?.error;

export const selectMultipleProductsError = (state) =>
  state.softwareRenewal?.multipleProductsError;

export const selectIncludeShipping = (state) =>
  state.softwareRenewal?.includeShipping || {};

export const selectCustomerEmail = (state) =>
  state.softwareRenewal?.licenseInfo?.customer_email;

export const selectLicenseInfo = (state) => {
  const licenseInfo = state.softwareRenewal?.licenseInfo;

  if (!licenseInfo) {
    return null;
  }

  return {
    customerName: licenseInfo.customer_name || null,
    expirationDate: licenseInfo.expiration_date || null,
    serialNumber: licenseInfo.serial || null,
    licenseSpan: licenseInfo.license_span || null,
    dongleLockedText: licenseInfo.dongle_locked_text || null,
    network: licenseInfo.network_license_text || null,
    evaluationVersion: licenseInfo.evaluation_version || false,
    products: licenseInfo.products || [],
    customerEmail: licenseInfo.customer_email || null,
    complementedBy: licenseInfo.complemented_by || null,
  };
};

export const dataForLicenseDisplay = createSelector(
  [selectLicenseInfo],
  (licenseInfo) => {
    if (!licenseInfo) {
      return {};
    }

    const { evaluationVersion, products } = licenseInfo;

    if (evaluationVersion) {
      return {
        ...licenseInfo,
        evaluationVersion,
      };
    }

    let displayOptionValues = {};
    products.forEach((product) => {
      const licenseSpan = getOptionValue(
        product.variant.option_values,
        'license_span',
      );
      const licenseType = getOptionValue(
        product.variant.option_values,
        'license-type',
      );
      const numberOfUsers = getOptionValue(
        product.variant.option_values,
        'num_of_users',
      );
      const activationMethod = getOptionValue(
        product.variant.option_values,
        'activation_method',
      );
      displayOptionValues = {
        licenseSpan: licenseSpan.presentation,
        numberOfUsers: numberOfUsers.presentation,
        activationMethod: activationMethod.presentation,
        licenseType: licenseType.presentation,
        productName: product.variant.name,
      };
    });

    return {
      ...licenseInfo,
      displayOptionValues,
    };
  },
);

export const dataForProductsToPurchase = createSelector(
  [selectLicenseInfo, selectIncludeShipping],
  (licenseInfo, includeShipping) => {
    if (!licenseInfo) {
      return {};
    }

    const { evaluationVersion, products } = licenseInfo;

    if (!products || evaluationVersion) {
      return {};
    }

    const totals = {
      subtotal: 0,
      discounts: [],
      total: 0,
      price: 0,
    };

    const result = [];

    products.forEach((product) => {
      const miniImage = product.images[0].mini_url;
      const { renewal_variant: renewalVariant } = product;

      let fullProductName = null;
      let isSoftwareRenewal = false;

      if (!renewalVariant) {
        return;
      }

      const { display_price: displayPrice, name: productName } = renewalVariant;

      const optionValues = {};

      if (productName === 'Software Renewal') {
        isSoftwareRenewal = true;
        const licenseTypeOptionValue = getOptionValue(
          renewalVariant.option_values,
          'license_type',
        );
        const [activationMethod, numberOfUsers, licenseSpan] =
          licenseTypeOptionValue.presentation.split(' - ');
        const licenseType =
          numberOfUsers === 'Single User' ? 'Standalone' : 'Network';
        optionValues.licenseSpan = `Subscription Period: ${licenseSpan.replace(/-/g, ' ')}`;
        optionValues.numberOfUsers = numberOfUsers;
        optionValues.licenseType = `${licenseType} ${activationMethod}`;
        fullProductName = getOptionValue(
          renewalVariant.option_values,
          'software_type',
        ).presentation;
      } else {
        optionValues.licenseType = getOptionValue(
          renewalVariant.option_values,
          'license-type',
        );
        const licenseSpan = getOptionValue(
          renewalVariant.option_values,
          'license_span',
        );
        const numberOfUsers = getOptionValue(
          renewalVariant.option_values,
          'num_of_users',
        );
        const activationMethod = getOptionValue(
          renewalVariant.option_values,
          'activation_method',
        );
        const licenseType = getOptionValue(
          renewalVariant.option_values,
          'license-type',
        );
        optionValues.licenseSpan = `Subscription Period: ${licenseSpan.presentation}`;
        optionValues.numberOfUsers = `${numberOfUsers.presentation}`;
        optionValues.licenseType = `${licenseType.presentation} ${activationMethod.presentation}`;
        fullProductName =
          renewalVariant.slug === 'viewmate-essentials'
            ? `${renewalVariant.name} (for former ViewMate Deluxe users)`
            : renewalVariant.name;
      }

      const variantPrice = parseFloat(product.renewal_variant.price); // 195
      totals.price += variantPrice; // 195

      const { applied_promotion: appliedPromotion } = product;

      if (appliedPromotion) {
        const {
          discount_percentage: discountPercentage,
          discounted_total: discountedTotal,
          prorated_discount: proratedDiscount,
          promotion_name: promotionName,
        } = appliedPromotion;

        if (discountedTotal > 0) {
          totals.discounts.push({
            promotionName,
            discountPercentage,
            discount: discountedTotal,
            displayDiscount: `$${parseFloat(discountedTotal).toFixed(2)}`,
          });

          if (proratedDiscount > 0) {
            totals.discounts.push({
              promotionName: 'Prorated Discount',
              discount: proratedDiscount,
              displayDiscount: `$${parseFloat(proratedDiscount).toFixed(2)}`,
            });
          }
        }
      }

      const variantId = product.renewal_variant?.id;
      const shippingOption = product.shippingOption || null;
      const override = variantId !== undefined ? includeShipping[variantId] : undefined;
      const shouldIncludeShipping = override ?? shippingOption?.defaultIncludeShipping;

      result.push({
        productName,
        fullProductName,
        miniImage,
        displayPrice,
        optionValues,
        variant: product.renewal_variant,
        appliedPromotion,
        isSoftwareRenewal,
        isDigitalDelivery: shippingOption?.isDigitalDelivery || false,
        shouldIncludeShipping,
        showIncludeShippingCheckbox:
          shippingOption?.canToggleIncludeShipping || false,
        includeShippingLocked:
          shippingOption?.includeShippingLocked || false,
        parentProductName: !isSoftwareRenewal && product.product ? product.product.name : null,
      });
    });

    totals.discountString = totals.discounts
      .reduce((acc, promotion) => acc + parseFloat(promotion.discount), 0)
      .toFixed(2);
    totals.displayDiscount = `$${totals.discountString}`;

    totals.total = parseFloat(totals.price) - parseFloat(totals.discountString);
    totals.totalString = parseFloat(totals.total).toFixed(2);
    totals.displayTotal = `$${totals.totalString}`;

    totals.subtotal = totals.price.toFixed(2);
    totals.displaySubtotal = `$${totals.subtotal}`;

    return {
      products: result,
      totals,
    };
  },
);
